import React, { useState } from 'react';
import { api } from '../api';
import { useNavigate, Link } from 'react-router-dom';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role] = useState('team_member'); // Kept for UI defaults; backend ignores and uses invite role
  const [reimbursementCurrency, setReimbursementCurrency] = useState('EUR');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    // Client-side validation
    const meetsLength = password.length >= 10;
    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSymbol = /[^A-Za-z0-9]/.test(password);
    const strong = meetsLength && hasLower && hasUpper && hasNumber && hasSymbol;
    if (!strong) {
      setError('Password does not meet strength requirements.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!inviteCode.trim()) {
      setError('Invite code is required.');
      setLoading(false);
      return;
    }

    try {
      await api.post('/api/auth/register', { 
        email, 
        password, 
        first_name: firstName,
        last_name: lastName,
        // role is assigned server-side from invite
        reimbursement_currency: reimbursementCurrency,
        invite_token: inviteCode.trim()
      });
      setSuccess('Registration successful!');
      // Redirect to login after successful registration
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: 480, background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)' }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 24 }}>Create your account</h2>
          <p style={{ margin: '6px 0 0 0', color: '#6b7280' }}>Join to manage your expenses</p>
        </div>

        {success && (
          <div style={{ background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', padding: '10px 12px', borderRadius: 8, marginBottom: 12 }}>
            {success}
          </div>
        )}
        {error && (
          <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', padding: '10px 12px', borderRadius: 8, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 14, color: '#374151', marginBottom: 6 }}>First name</label>
            <input value={firstName} onChange={e => setFirstName(e.target.value)} type="text" required style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 14, color: '#374151', marginBottom: 6 }}>Last name</label>
            <input value={lastName} onChange={e => setLastName(e.target.value)} type="text" required style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb' }} />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={{ display: 'block', fontSize: 14, color: '#374151', marginBottom: 6 }}>Invite code</label>
          <input value={inviteCode} onChange={e => setInviteCode(e.target.value)} type="text" required placeholder="Paste your invite token" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb' }} />
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={{ display: 'block', fontSize: 14, color: '#374151', marginBottom: 6 }}>Email</label>
          <input value={email} onChange={e => setEmail(e.target.value)} type="email" required style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb' }} />
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={{ display: 'block', fontSize: 14, color: '#374151', marginBottom: 6 }}>Password</label>
          <input value={password} onChange={e => setPassword(e.target.value)} type="password" required style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb' }} />
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={{ display: 'block', fontSize: 14, color: '#374151', marginBottom: 6 }}>Confirm password</label>
          <input value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} type="password" required style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb' }} />
        </div>

        {/* Password strength helper */}
        {(() => {
          const meetsLength = password.length >= 10;
          const hasLower = /[a-z]/.test(password);
          const hasUpper = /[A-Z]/.test(password);
          const hasNumber = /[0-9]/.test(password);
          const hasSymbol = /[^A-Za-z0-9]/.test(password);
          const item = (ok: boolean, label: string) => (
            <li key={label} style={{ color: ok ? '#065f46' : '#b91c1c' }}>{ok ? '✓' : '•'} {label}</li>
          );
          return (
            <div style={{ marginTop: 8, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
              <div style={{ fontSize: 12, color: '#374151', marginBottom: 6 }}>Password must include:</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.6 }}>
                {item(meetsLength, 'At least 10 characters')}
                {item(hasLower, 'Lowercase letter')}
                {item(hasUpper, 'Uppercase letter')}
                {item(hasNumber, 'Number')}
                {item(hasSymbol, 'Symbol')}
              </ul>
              {confirmPassword && (
                <div style={{ marginTop: 6, fontSize: 12, color: password === confirmPassword ? '#065f46' : '#b91c1c' }}>
                  {password === confirmPassword ? '✓ Passwords match' : 'Passwords do not match'}
                </div>
              )}
            </div>
          );
        })()}
        <div style={{ marginTop: 12 }}>
          <label style={{ display: 'block', fontSize: 14, color: '#374151', marginBottom: 6 }}>Reimbursement currency</label>
          <select value={reimbursementCurrency} onChange={e => setReimbursementCurrency(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <option value="EUR">EUR (Euro)</option>
            <option value="USD">USD (US Dollar)</option>
          </select>
        </div>

        {(() => {
          const meetsLength = password.length >= 10;
          const hasLower = /[a-z]/.test(password);
          const hasUpper = /[A-Z]/.test(password);
          const hasNumber = /[0-9]/.test(password);
          const hasSymbol = /[^A-Za-z0-9]/.test(password);
          const strong = meetsLength && hasLower && hasUpper && hasNumber && hasSymbol;
          const canSubmit = strong && password === confirmPassword && !!inviteCode.trim() && !loading;
          return (
            <button type="submit" disabled={!canSubmit} style={{ marginTop: 16, width: '100%', padding: '10px 12px', backgroundColor: canSubmit ? '#3b82f6' : '#93c5fd', color: 'white', border: 'none', borderRadius: 8, cursor: canSubmit ? 'pointer' : 'not-allowed', fontWeight: 600 }}>
              {loading ? 'Creating account…' : 'Create account'}
            </button>
          );
        })()}

        <p style={{ marginTop: 16, color: '#6b7280', textAlign: 'center' }}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </form>
    </div>
  );
};

export default Register;
